/**
 * Selector de países latinoamericanos
 * 
 * Componente que obtiene la lista de países desde una API externa y la filtra
 * para mostrar únicamente países latinoamericanos. Incluye estados de carga,
 * error y manejo de selección.
 * 
 * @component
 * @example
 * // Uso básico
 * <CountrySelector 
 *   onSelect={(country) => console.log(country)}
 *   selectedCountryValue={selectedCountry}
 * />
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Function} props.onSelect - Función callback que se ejecuta al seleccionar un país
 * @param {string} props.selectedCountryValue - Valor del país actualmente seleccionado
 * 
 * @author Ana Lizcano y Alejandro
 * @version 1.0.1
 * @since 2025-07-10
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import { createPortal } from "react-dom";

// Constante con países latinoamericanos
import { LATIN_AMERICAN_COUNTRIES } from "../../utils/latinAmericanCountries";

/**
 * Componente principal del selector de países
 * 
 * Maneja la carga asíncrona de países desde API externa,
 * filtra por países latinoamericanos y proporciona interfaz de selección.
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Function} props.onSelect - Callback para manejar selección de país
 * @param {string} props.selectedCountryValue - País actualmente seleccionado
 * @returns {JSX.Element} Componente selector de países
 */
function CountrySelector({ onSelect, selectedCountryValue }) {
  // Estados para manejo de datos y UI
  return (
    <TextField
      fullWidth
      select
      label="País"
      value={selectedCountryValue || ""}
      onChange={(event) => {
        onSelect(event.target.value);
      }}
      variant="standard"
      SelectProps={{
        native: false,
        MenuProps: {
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 9999,
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        },
      }}
      sx={{
        '& .MuiInput-underline:before': {
          borderBottomColor: 'rgba(0, 0, 0, 0.42)',
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderBottomColor: 'rgba(0, 0, 0, 0.87)',
        },
        '& .MuiInput-underline:after': {
          borderBottomColor: '#1976d2',
        },
        zIndex: 1,
        position: 'relative',
      }}
    >
      <MenuItem value="">
        <em>Seleccione un país</em>
      </MenuItem>
      {LATIN_AMERICAN_COUNTRIES.map((country) => (
        <MenuItem key={country} value={country}>
          {country}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * Validación de tipos de propiedades
 * @type {Object}
 */
CountrySelector.propTypes = {
  /** Función callback que se ejecuta al seleccionar un país */
  onSelect: PropTypes.func.isRequired,
  /** Valor del país actualmente seleccionado */
  selectedCountryValue: PropTypes.string,
};

/**
 * Valores por defecto de las propiedades
 * @type {Object}
 */
CountrySelector.defaultProps = {
  selectedCountryValue: "",
};

export default CountrySelector;